import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../../components/Footer';
import { ArrowLeft, CheckCircle, MapPin, Phone } from 'lucide-react';

const locations = [
  'Scarsdale', 'Greenwich', 'Rye', 'Harrison', 'Mamaroneck', 'Bedford',
  'Chappaqua', 'Bronxville', 'Larchmont', 'Purchase'
];

const features = [
  {
    title: 'Custom Pool Design',
    description: 'Bespoke pool designs tailored to your property and lifestyle preferences.'
  },
  {
    title: 'Infinity Edges',
    description: 'Stunning infinity edge designs that create visual drama and elegance.'
  },
  {
    title: 'Smart Controls',
    description: 'Advanced automation systems for temperature, lighting, and maintenance.'
  },
  {
    title: 'LED Lighting',
    description: 'Custom lighting solutions for nighttime ambiance and safety.'
  },
  {
    title: 'Water Features',
    description: 'Integrated waterfalls, fountains, and other custom water features.'
  },
  {
    title: 'Spa Integration',
    description: 'Luxury spa features including hydrotherapy jets and heating systems.'
  }
];

const portfolioImages = [
  {
    url: 'https://images.unsplash.com/photo-1572331165267-854da2b10ccc?auto=format&fit=crop&q=80&w=1200',
    location: 'Scarsdale Infinity Pool'
  },
  {
    url: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?auto=format&fit=crop&q=80&w=1200',
    location: 'Greenwich Spa Retreat'
  },
  {
    url: 'https://images.unsplash.com/photo-1575429198097-0414ec08e8cd?auto=format&fit=crop&q=80&w=1200',
    location: 'Rye Pool & Spa'
  }
];

export function PoolSpaPage() {
  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>Luxury Pool & Spa Design in Westchester County | Golden Touch</title>
        <meta name="description" content="Premium pool and spa design services in Westchester County & Greenwich. Specializing in custom pools, spas, and water features with expert craftsmanship." />
        <meta name="keywords" content="pool design Westchester, custom spa Greenwich, luxury pool builder Scarsdale, infinity pool Rye" />
        <link rel="canonical" href="https://renovationwestchester.com/services/pool-spa-design-westchester" />
      </Helmet>


      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Services</span>
          </Link>

          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">
                Luxury Pool & Spa Design in Westchester
              </h1>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Transform your outdoor space into a private oasis with our custom pool and spa designs. 
                Serving distinguished homes throughout Westchester County and Lower Connecticut with 
                unparalleled craftsmanship.
              </p>
              <div className="flex items-center gap-2 text-yellow-400 mb-8">
                <MapPin className="h-5 w-5" />
                <span className="text-sm tracking-wider">Westchester County & Greenwich Area</span>
              </div>
              <Link to="/consultation" className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900">
                <Phone className="h-4 w-4" />
                <span className="text-sm tracking-wider">Schedule a Consultation</span>
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={portfolioImages[0].url}
                alt="Luxury Pool Design in Westchester"
                className="w-full h-full object-cover"
                width="1200"
                height="900"
              />
            </div>
          </div>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Premium Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-zinc-800/30 p-8">
                  <CheckCircle className="h-8 w-8 text-yellow-400 mb-4" />
                  <h3 className="text-xl text-zinc-100 mb-4">{feature.title}</h3>
                  <p className="text-zinc-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Areas Served</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {locations.map((location) => (
                <Link
                  key={location}
                  to={`/areas/${location.toLowerCase()}`}
                  className="block bg-zinc-800/30 p-4 text-center border border-transparent hover:border-yellow-400/40 transition-colors"
                >
                  <MapPin className="h-5 w-5 text-yellow-400 mx-auto mb-2" />
                  <span className="text-zinc-300">{location}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-3xl font-light text-zinc-100 mb-8">Ready to Create Your Perfect Oasis?</h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              Let's discuss your vision for the perfect outdoor retreat. Our expert team is ready 
              to create a stunning pool and spa design that transforms your property.
            </p>
            <Link to="/consultation" className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900">
              <Phone className="h-4 w-4" />
              <span className="text-sm tracking-wider">Schedule Your Consultation</span>
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}